// Implements: F-IDN-026
/**
 * The reads and writes of Invitaciones. Issuing N addresses sends N requests one after the other
 * (never in parallel, never retried) and answers what each one got: the issued invitation with its
 * clear token, or the server's refusal. Copying a link ROTATES it (`POST .../link`): the new token
 * is the only one that works from then on.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import type { InvitationList, IssuedInvitation, OrgRole, TeamRole } from "../../../types/organization";
import { fetchInvitations, issueInvitation, revokeInvitation, rotateInvitationLink } from "../api/organizationApi";
import { invitationsKey, refreshOrganization } from "./keys";

export function useInvitationList(organizationId: string): UseQueryResult<InvitationList, ApiError> {
  return useQuery<InvitationList, ApiError>({
    queryKey: invitationsKey(organizationId),
    queryFn: ({ signal }) => fetchInvitations(organizationId, signal),
  });
}

export interface InviteBatch {
  readonly emails: readonly string[];
  readonly role: OrgRole;
  readonly teamId: string | null;
  readonly teamRole: TeamRole | null;
  /** Owner 2026-10-19: after "Convertir a <name> en miembro", the lead the question named. */
  readonly replaceLeadUserId?: string;
}

export type IssueOutcome =
  | { readonly email: string; readonly issued: IssuedInvitation; readonly error?: undefined }
  | { readonly email: string; readonly error: ApiError; readonly issued?: undefined };

export function useIssueInvitations(organizationId: string): UseMutationResult<IssueOutcome[], ApiError, InviteBatch> {
  const queryClient = useQueryClient();
  return useMutation<IssueOutcome[], ApiError, InviteBatch>({
    mutationFn: async ({ emails, role, teamId, teamRole, replaceLeadUserId }) => {
      const outcomes: IssueOutcome[] = [];
      // The key travels only after the question was answered with "Convertir…".
      const handOver = replaceLeadUserId === undefined ? {} : { replace_lead_user_id: replaceLeadUserId };
      for (const email of emails) {
        try {
          const request = { email, role, team_id: teamId, team_role: teamRole, ...handOver };
          outcomes.push({ email, issued: await issueInvitation(organizationId, request) });
        } catch (cause) {
          outcomes.push({ email, error: asApiError(cause) });
        }
      }
      return outcomes;
    },
    onSettled: () => refreshOrganization(queryClient, organizationId),
  });
}

export function useRotateInvitationLink(organizationId: string): UseMutationResult<IssuedInvitation, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<IssuedInvitation, ApiError, string>({
    mutationFn: (invitationId) => rotateInvitationLink(organizationId, invitationId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: invitationsKey(organizationId) }),
  });
}

export function useRevokeInvitation(organizationId: string): UseMutationResult<null, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<null, ApiError, string>({
    mutationFn: async (invitationId) => {
      await revokeInvitation(organizationId, invitationId);
      return null;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: invitationsKey(organizationId) }),
  });
}
